import { isGaplessSetNextEnabled, rendererSupportsSetNext } from './gaplessSetNext';
import { getRendererGaplessEnabled, hasRendererGaplessPref } from '../services/gaplessPlaybackSettings'; import { clampFormatIdToLearnedCeiling } from './formatCeiling';

export interface TransportTrack {
  id?: string;
  title?: string;
  artist?: string;
  album?: string;
  artUrl?: string;
  artworkUrl?: string;
  duration?: number;
  formatId?: number;
  qobuzTrackId?: string;
  serviceTrackId?: string;
  source?: string;
  file_path?: string;
  machine_id?: string;
  bitDepth?: number;
  sampleRate?: number;
  /** Channel count for the file. Plumbed into DIDL nrAudioChannels for UPnP renderers. */
  channels?: number;
  format?: string;
  albumId?: string;
  streamUrl?: string;
}

export type ActiveSource = 'library' | 'radio';
export type RepeatMode = 'off' | 'one' | 'all';

/** Zone fields used to identify renderers that must not receive SetNextAVTransportURI prefetch. */
export type GaplessPrefetchZoneHint = {
  id?: string;
  name?: string;
  type?: string;
  capabilities?: { fixedVolume?: boolean; volumeNotControllable?: boolean };
  metadata?: Record<string, unknown>;
};

function isRendererGaplessDisabled(
  renderer: { id: string; gapless_enabled?: boolean } | null | undefined,
  zone?: GaplessPrefetchZoneHint | null,
): boolean {
  const rendererId = renderer?.id || zone?.id;
  if (rendererId && hasRendererGaplessPref(rendererId)) {
    return !getRendererGaplessEnabled(rendererId);
  }
  if (renderer?.gapless_enabled === false) return true;
  if (zone?.metadata?.gapless_enabled === false) return true;
  return false;
}

/**
 * Legacy name/heuristic skip list — used when GAPLESS_SETNEXT flag is OFF so prod
 * behavior stays byte-identical until the flag is enabled on the Mini.
 */
function shouldSkipGaplessPrefetchLegacy(
  renderer: { id: string; name?: string; gapless_enabled?: boolean } | null | undefined,
  zone?: GaplessPrefetchZoneHint | null,
): boolean {
  if (!renderer?.id) return false;

  if (isRendererGaplessDisabled(renderer, zone)) return true;

  const id = renderer.id;
  if (id.startsWith('bluesound-') || id.startsWith('raat-')) return true;

  const zoneType = (zone?.type || '').toLowerCase();
  if (zoneType === 'psaudio') return true;

  const transport = String(zone?.metadata?.transport_type ?? '').toLowerCase();
  if (transport === 'psaudio') return true;

  const name = (renderer.name || zone?.name || '').toLowerCase();
  if (name.includes('pmg')) return true;

  const mfr = String(zone?.metadata?.manufacturer ?? '').toLowerCase();
  const model = String(zone?.metadata?.model ?? '').toLowerCase();

  const isPsAudioMfr =
    mfr.includes('ps audio') ||
    mfr.includes('psaudio') ||
    mfr.includes('conversdigital');

  const isAirLensModel = model.includes('airlens');
  const isAirLensName = name.includes('airlens');

  if (isPsAudioMfr && (isAirLensModel || isAirLensName)) return true;
  if (!mfr && !model && isAirLensName) return true;

  if (name.includes('ideon') || mfr.includes('ideon')) return true;

  if (name.includes('grimm') || mfr.includes('grimm') || model.includes('mu2')) return true;

  return false;
}

/**
 * Renderers that destabilize when sent gapless SetNextAVTransportURI prefetch.
 * When GAPLESS_SETNEXT is enabled: skip iff !avt_supports_set_next (capability only).
 * When flag is OFF: legacy name/heuristic list (prod no-op).
 */
export function shouldSkipGaplessPrefetch(
  renderer: { id: string; name?: string; gapless_enabled?: boolean; avt_supports_set_next?: boolean } | null | undefined,
  zone?: GaplessPrefetchZoneHint | null,
): boolean {
  if (!isGaplessSetNextEnabled()) {
    return shouldSkipGaplessPrefetchLegacy(renderer, zone);
  }

  if (!renderer?.id) return false;

  if (isRendererGaplessDisabled(renderer, zone)) return true;

  return !rendererSupportsSetNext(renderer, zone);
}

/**
 * Renderers where the Connector uses duration-based track-end (no reliable
 * GetTransportInfo / GetPositionInfo). Matches renderer_skips_transport_info_poll in Rust.
 *
 * NOT gated by GAPLESS_SETNEXT — position/advance classification must stay identical
 * with the flag on or off; the flag only gates SetNext prefetch and boundary handoff.
 */
export function isTimerBasedRenderer(
  renderer: { id: string; name?: string; avt_supports_set_next?: boolean } | null | undefined,
  zone?: GaplessPrefetchZoneHint | null,
): boolean {
  return shouldSkipGaplessPrefetchLegacy(renderer, zone);
}

/** PS Audio / AirLens / Ideon / Grimm MU2-class — deep-buffer, timer-monitored renderers. */
export function isDeepBufferTimerRenderer(
  renderer: { id: string; name?: string; avt_supports_set_next?: boolean } | null | undefined,
  zone?: GaplessPrefetchZoneHint | null,
): boolean {
  return isTimerBasedRenderer(renderer, zone);
}

/**
 * Returns true for renderers that only support CD quality (16-bit/44.1kHz).
 * Sonos devices max at 16-bit/48kHz — sending hi-res causes
 * TRANSITIONING -> STOPPED immediately.
 */
export function isLimitedRenderer(
  renderer: { id: string; name?: string; manufacturer?: string } | null | undefined,
): boolean {
  if (!renderer) return false;
  const n = (renderer.name || '').toLowerCase();
  const m = ((renderer as any).manufacturer || '').toLowerCase();
  return n.includes('sonos') || n.includes('rincon')
    || m.includes('sonos') || m.includes('rincon');
}

/**
 * Determine the Qobuz format_id for a given renderer.
 * Limited renderers (Sonos) get format 6 (CD 16-bit/44.1kHz).
 * Everything else gets format 27 (hi-res 24-bit/192kHz).
 *
 * The format is a function of the RENDERER capability, not the track.
 * Qobuz automatically downgrades if a track doesn't support hi-res.
 * Never use track.formatId here — it leaks Sonos format contamination
 * across zone switches and localStorage-restored queues.
 */
export function getFormatIdForRenderer(
  renderer: { id: string; name?: string; manufacturer?: string } | null | undefined,
): number {
  return clampFormatIdToLearnedCeiling(renderer, isLimitedRenderer(renderer) ? 6 : 27); // learned per-device ceiling (formatCeiling.ts)
}
